import {
  ControlGroup,
  FormGroup,
  HTMLSelect,
  IHTMLSelectProps,
  IInputGroupProps,
  InputGroup,
  Intent,
  INumericInputProps,
  IOptionProps,
  Tag
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import Client from '../../../../../client'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import { APISelect } from '../../../../../components/selects/APISelect'
import { renderError, stylizeTitle } from '../Helpers'
import { ConditionOptions, FB_GTC_DURATION, GenderOptions, MaterialOptions } from './constants'
import { FormChangeable, FormErrorable } from './types'

export const PriceInput: React.FC<INumericInputProps & FormChangeable & FormErrorable> = props => {
  return (
    <FormGroup
      label={stylizeTitle('Price', props.error)}
      intent={props.error ? Intent.DANGER : Intent.NONE}
      helperText={renderError(props.error)}
    >
      <NullSafeNumericInput
        {...props}
        leftIcon={IconNames.DOLLAR}
        min={0}
        minorStepSize={0.01}
        fill
        intent={props.error ? Intent.DANGER : Intent.NONE}
        onValueChange={(_, value) => props.onInputChange({ name: 'price', value })}
      />
    </FormGroup>
  )
}

export const PageFeedInput: React.FC<
  IHTMLSelectProps & FormChangeable & FormErrorable & { helperText?: string; blankOption?: IOptionProps }
> = props => {
  return (
    <FormGroup
      label={stylizeTitle('Facebook Page', props.error)}
      intent={props.error ? Intent.DANGER : Intent.NONE}
      helperText={renderError(props.error) || props.helperText}
    >
      <APISelect
        fill
        disabled
        blankOption={props.blankOption || { label: 'Select a Facebook Page...', value: null }}
        {...props}
        onChange={e => props.onInputChange({ name: 'facebookPageFeedId', value: e.target.value })}
        request={Client.getFacebookPageFeeds}
        process={d => d.facebookPageFeeds.map(feed => ({ label: feed.pageName, value: feed.id }))}
      />
    </FormGroup>
  )
}

export interface IDurationInputOption extends IOptionProps {
  placeholder?: string
}

export const DurationInput: React.FC<
  INumericInputProps & FormChangeable & FormErrorable & { additionalOptions?: IDurationInputOption[] }
> = props => {
  const fixedLengthListing = props.value === '' || +props.value > 0
  const sanatizeValue = v => {
    if (v === '' || +v === 0) return ''
    return v && parseInt(v.replace('-', ''))
  }
  const options: IDurationInputOption[] = [
    {
      label: 'Fixed-length',
      value: fixedLengthListing ? String(props.value) : '1',
      placeholder: 'Enter a duration...'
    },
    {
      label: "Good 'Til Cancelled",
      value: String(FB_GTC_DURATION),
      placeholder: 'List until sold or delisted'
    },
    ...(props.additionalOptions || [])
  ]
  const selectedOption: any = options.find(e => e.value === String(props.value)) || {}
  return (
    <FormGroup
      label={stylizeTitle('Duration', props.error)}
      intent={props.error ? Intent.DANGER : Intent.NONE}
      labelInfo={renderError(props.error)}
    >
      <ControlGroup>
        <HTMLSelect
          options={options}
          value={String(props.value)}
          onChange={e => props.onInputChange({ name: 'duration', value: e.target.value })}
        />
        {fixedLengthListing && (
          <NullSafeNumericInput
            min={1}
            minorStepSize={1}
            majorStepSize={3}
            fill
            placeholder="Enter a number"
            rightElement={fixedLengthListing && <Tag minimal>day{+props.value === 1 ? '' : 's'}</Tag>}
            {...props}
            disabled={props.disabled}
            onValueChange={(_, v) =>
              props.onInputChange({
                name: 'duration',
                value: fixedLengthListing ? sanatizeValue(v) : FB_GTC_DURATION
              })
            }
          />
        )}
        {!fixedLengthListing && <InputGroup disabled placeholder={selectedOption.placeholder} fill />}
      </ControlGroup>
    </FormGroup>
  )
}

export const GenderInput: React.FC<IHTMLSelectProps & FormChangeable & FormErrorable> = props => {
  return (
    <FormGroup
      label={stylizeTitle('Gender', props.error)}
      helperText={renderError(props.error)}
      intent={props.error ? Intent.DANGER : Intent.NONE}
    >
      <HTMLSelect
        options={GenderOptions}
        fill
        {...props}
        onChange={e => props.onInputChange({ name: 'gender', value: e.target.value })}
      />
    </FormGroup>
  )
}
export const ConditionSelect: React.FC<IHTMLSelectProps> = props => {
  return <HTMLSelect options={ConditionOptions} {...props} />
}

export const ConditionInput: React.FC<IHTMLSelectProps & FormChangeable & FormErrorable> = props => {
  return (
    <FormGroup
      label={stylizeTitle('Condition', props.error)}
      helperText={renderError(props.error)}
      intent={props.error ? Intent.DANGER : Intent.NONE}
    >
      <ConditionSelect
        {...props}
        onChange={e => props.onInputChange({ name: 'condition', value: e.target.value })}
        fill
      />
    </FormGroup>
  )
}

export const MaterialInput: React.FC<IHTMLSelectProps & FormChangeable> = props => {
  return (
    <FormGroup label="Material">
      <HTMLSelect
        options={MaterialOptions}
        {...props}
        fill
        onChange={e => props.onInputChange({ name: 'material', value: e.target.value })}
      />
    </FormGroup>
  )
}

export const ColorInput: React.FC<IInputGroupProps & FormChangeable & FormErrorable> = props => {
  const intent = props.error ? Intent.DANGER : Intent.NONE
  return (
    <FormGroup label="Color">
      <InputGroup
        intent={intent}
        placeholder="e.g. Navy Blue, Turqoise, etc."
        {...props}
        onChange={e => props.onInputChange({ name: 'color', value: e.target.value })}
      />
    </FormGroup>
  )
}

export const PatternInput: React.FC<IInputGroupProps & FormChangeable & FormErrorable> = props => {
  const intent = props.error ? Intent.DANGER : Intent.NONE
  return (
    <FormGroup label="Pattern">
      <InputGroup
        intent={intent}
        placeholder="e.g. Flannel, Polka dots, etc."
        {...props}
        onChange={e => props.onInputChange({ name: 'pattern', value: e.target.value })}
      />
    </FormGroup>
  )
}

export const BrandInput: React.FC<IInputGroupProps & FormChangeable & FormErrorable> = props => {
  const intent = props.error ? Intent.DANGER : Intent.NONE
  return (
    <FormGroup label={stylizeTitle('Brand', props.error)} helperText={renderError(props.error)} intent={intent}>
      <InputGroup
        value={props.value}
        intent={intent}
        placeholder="e.g. Adidas, Apple, etc."
        onChange={e => props.onInputChange({ name: 'brand', value: e.target.value })}
      />
    </FormGroup>
  )
}

export const SizeInput: React.FC<IInputGroupProps & FormChangeable> = props => {
  return (
    <FormGroup label="Size">
      <InputGroup
        value={props.value}
        placeholder="e.g. 10, Med, XXL, etc."
        onChange={e => props.onInputChange({ name: 'size', value: e.target.value })}
      />
    </FormGroup>
  )
}
